/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import type { HeatmapCell } from './rfSimulation'
import { pointInFloorBounds } from './floorBounds'
import { apsForRfAnalysis, optimizationGrid } from './rfFloorMetrics'
import {
  coChannelRecommendationCopy,
  coChannelWarningLevel,
  evaluateInterferencePlan,
} from './rfInterferencePolicy'
import {
  combinedInterferenceFix,
  describeFix,
  findGapApPosition,
  hasAutoFix,
  interferencePowerFix,
  isServiceableApSite,
  relocatePairFix,
} from './rfRecommendationFixes'
import type { FloorGeometry, FloorRecommendation, PlacedAp } from './types'

const PRIORITY_ORDER: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 }

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

function closeApPairs(aps: PlacedAp[], floorWidthM: number, floorHeightM: number) {
  const pairs: { a: PlacedAp; b: PlacedAp; distM: number }[] = []
  for (let i = 0; i < aps.length; i++) {
    for (let j = i + 1; j < aps.length; j++) {
      const distM = distanceM(aps[i].x, aps[i].y, aps[j].x, aps[j].y, floorWidthM, floorHeightM)
      if (distM < 14) pairs.push({ a: aps[i], b: aps[j], distM })
    }
  }
  return pairs.sort((x, y) => x.distM - y.distM)
}

function sortRecs(recs: FloorRecommendation[]): FloorRecommendation[] {
  return [...recs].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9),
  )
}

function clampObstacle(n: number) {
  return Math.min(92, Math.max(8, n))
}

/** Live RF recommendations from the current floor layout and heatmap grid. */
export function computeRfRecommendations(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  grid?: HeatmapCell[],
): FloorRecommendation[] {
  if (aps.length === 0) return []

  const analyzedAps = apsForRfAnalysis(aps, geometry, floorWidthM, floorHeightM)
  const interfPlan = evaluateInterferencePlan(analyzedAps, geometry, floorWidthM, floorHeightM)

  const allCells = grid ?? optimizationGrid(aps, geometry, floorWidthM, floorHeightM)
  const heatmapGrid = allCells.filter(c => pointInFloorBounds(c.x, c.y, geometry))
  const recs: FloorRecommendation[] = []
  const serviceable = heatmapGrid.filter(c =>
    isServiceableApSite(c.x, c.y, geometry, floorWidthM, floorHeightM),
  )
  const total = Math.max(1, serviceable.length)

  const weak = serviceable.filter(c => c.rssi < -75).length
  const fair = serviceable.filter(c => c.rssi >= -75 && c.rssi < -65).length
  const highInterf = heatmapGrid.filter(c => c.interference >= 55).length
  const severeInterf = heatmapGrid.filter(c => c.interference >= 75).length
  const highInterfPct = (highInterf / total) * 100
  const severeInterfPct = (severeInterf / total) * 100

  const pairs = closeApPairs(analyzedAps, floorWidthM, floorHeightM)

  if (weak > total * 0.15) {
    const gapPos = findGapApPosition(aps, heatmapGrid, floorWidthM, floorHeightM, geometry)
    recs.push({
      id: 'coverage-hole',
      priority: 'HIGH',
      title: 'Coverage holes detected',
      detail: `${Math.round((weak / total) * 100)}% of serviceable floor below −75 dBm (${weak} cells) — add an AP or increase TX power in weak zones (not inside lifts or racks).`,
      action: 'add_ap',
      metricPct: Math.round((weak / total) * 100),
      fix: gapPos ? { newApAt: gapPos } : undefined,
    })
  } else if (aps.length === 1 && weak + fair > total * 0.35) {
    const gapPos =
      findGapApPosition(aps, heatmapGrid, floorWidthM, floorHeightM, geometry) ?? { x: 50, y: 50 }
    recs.push({
      id: 'single-ap-stretch',
      priority: 'MEDIUM',
      title: 'Single AP may be undersized',
      detail: `One AP covers ${Math.round(((weak + fair) / total) * 100)}% marginal/weak cells — consider a second AP for this floor.`,
      action: 'add_ap',
      fix: { newApAt: gapPos },
    })
  }

  const coLevel = coChannelWarningLevel(highInterfPct, severeInterfPct, interfPlan)
  if (coLevel !== 'none' && analyzedAps.length >= 2) {
    const pct = Math.round(highInterfPct)
    const pair = pairs[0]
    const pairIds = pair ? [pair.a.id, pair.b.id] : analyzedAps.slice(0, 2).map(a => a.id)
    const copy = coChannelRecommendationCopy(highInterfPct, interfPlan, coLevel)
    const offerFix = coLevel === 'action' && !interfPlan.tuned
    recs.push({
      id: 'co-channel',
      priority: copy.priority,
      title: copy.title,
      detail: copy.detail,
      action: 'adjust_power',
      metricPct: pct,
      fix: offerFix
        ? pair
          ? combinedInterferenceFix(analyzedAps, pair, floorWidthM, floorHeightM, geometry)
          : interferencePowerFix(analyzedAps, pairIds)
        : undefined,
    })
  }

  if (analyzedAps.length >= 2 && severeInterf > total * 0.06 && coLevel === 'action') {
    const pair = pairs[0]
    recs.push({
      id: 'sticky-risk',
      priority: 'MEDIUM',
      title: 'Sticky-client risk',
      detail: `${Math.round((severeInterf / total) * 100)}% of cells where two APs are within ~6 dB — enable band steering or adjust placement so one AP clearly dominates each area.`,
      action: 'relocate_ap',
      fix: pair ? relocatePairFix(pair.a, pair.b, floorWidthM, floorHeightM, geometry) : undefined,
    })
  }

  if (pairs.length > 0 && highInterf > total * 0.08 && coLevel === 'action' && !interfPlan.tuned) {
    const { a, b, distM } = pairs[0]
    recs.push({
      id: 'ap-too-close',
      priority: distM < 8 ? 'HIGH' : 'MEDIUM',
      title: `${a.ap} and ${b.ap} are very close`,
      detail: `${distM.toFixed(1)} m apart on ${geometry.bandGHz} GHz — move ≥12 m apart, use different channels, or lower TX power on one AP.`,
      action: 'relocate_ap',
      fix: relocatePairFix(a, b, floorWidthM, floorHeightM, geometry),
    })
  }

  const hotTx = analyzedAps.filter(ap => (ap.txPowerDbm ?? 15) >= 20)
  if (hotTx.length >= 2 && pairs.some(p => p.distM < 10) && highInterf > total * 0.12) {
    recs.push({
      id: 'tx-too-high',
      priority: 'MEDIUM',
      title: 'High TX power amplifies overlap',
      detail: `${hotTx.map(ap => ap.ap).join(', ')} at ≥20 dBm near other APs — try 15–17 dBm to shrink cell edges and reduce co-channel noise.`,
      action: 'adjust_power',
      fix: interferencePowerFix(aps, hotTx.map(ap => ap.id)),
    })
  }

  if (geometry.walls.length === 0 && aps.length >= 2) {
    recs.push({
      id: 'walls',
      priority: 'LOW',
      title: 'Define walls for accurate RF',
      detail: 'No walls drawn — interference and coverage ignore partitions. Add walls with height for glass dividers and low separators.',
      action: 'draw_walls',
      fix: { perimeterWalls: true, enableLayer: 'walls' },
    })
  }

  if (geometry.obstacles.length === 0 && aps.some(ap => (ap.mountHeightM ?? 3) <= 2.5)) {
    const anchor = aps.find(ap => (ap.mountHeightM ?? 3) <= 2.5) ?? aps[0]
    recs.push({
      id: 'obstacles',
      priority: 'LOW',
      title: 'Add racks and obstacles',
      detail: 'Low or wall-mounted APs are modeled without metal obstructions — add server racks and cabinets for realistic shadowing.',
      action: 'draw_obstacles',
      fix: {
        obstacleType: 'server_rack',
        obstacleAt: { x: clampObstacle(anchor.x + 8), y: clampObstacle(anchor.y) },
        switchTool: 'select',
      },
    })
  }

  return sortRecs(dedupeRecommendations(enrichRecommendations(recs, aps, geometry.bandGHz)))
}

const OVERLAP_IDS = new Set(['co-channel', 'sticky-risk', 'ap-too-close'])

function dedupeRecommendations(recs: FloorRecommendation[]): FloorRecommendation[] {
  const overlap = recs.filter(r => OVERLAP_IDS.has(r.id))
  if (overlap.length <= 1) return recs
  const primary =
    overlap.find(r => r.id === 'co-channel' && hasAutoFix(r))
    ?? overlap.find(r => hasAutoFix(r))
    ?? overlap[0]
  const drop = new Set(overlap.filter(r => r.id !== primary.id).map(r => r.id))
  return recs.filter(r => !drop.has(r.id))
}

function enrichRecommendations(
  recs: FloorRecommendation[],
  aps: PlacedAp[],
  bandGHz: number,
): FloorRecommendation[] {
  return recs.map(r => ({
    ...r,
    category: categoryFor(r.id),
    fixPreview: describeFix(r, aps, bandGHz),
  }))
}

function categoryFor(id: string): FloorRecommendation['category'] {
  if (id === 'coverage-hole' || id === 'single-ap-stretch') return 'coverage'
  if (OVERLAP_IDS.has(id) || id === 'tx-too-high') return 'interference'
  if (id === 'walls' || id === 'obstacles') return 'modeling'
  return 'capacity'
}
